<script setup lang="ts">
  import { type StakeholderContactOther } from '@gat-ship/types'
  import { useFormatting } from '@gat-ship/utils'
  import {
    DataListItem,
    DataListItemAction,
    DataListItemActions,
    DataListItemTitle,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem
  } from '@gat-ship/vue-components'
  import ContactDetailsBtnShow from '../commands/buttons/ContactDetailsBtnShow.vue'
  import ContactPersonBtnDelete from '../commands/buttons/ContactPersonBtnDelete.vue'
  import { useContactProvider } from '../../../composables/useStakeholderContacts'
  import { testIds } from '@gat-ship/types'
  import {
    IconError,
    IconEye,
    IconEdit,
    IconTrash,
    IconMail,
    IconSmartphone,
    IconPhone
  } from '@gat-ship/icons'

  const props = withDefaults(
    defineProps<{
      contact: StakeholderContactOther
      showContactType?: boolean
      errorMessages?: string[]
    }>(),
    {
      showContactType: false,
      errorMessages: () => []
    }
  )

  const { formatPhoneNumber } = useFormatting()

  const emit = defineEmits<{
    (e: 'click:item', contact: StakeholderContactOther): void
  }>()

  const { modals } = useContactProvider()
</script>
<template>
  <DataListItem
    :item="props.contact"
    :options="{
      clickable: true
    }"
    :data-testid="
      testIds.stakeholders.contacts.list.contactPointId(props.contact.id)
    "
    @click="emit('click:item', props.contact)"
  >
    <DataListItemTitle class="flex flex-row items-center gap-1">
      <span>
        {{ props.contact.otherName }}
      </span>
      <template v-if="showContactType">
        <span class="text-muted-foreground text-xs">•</span>
        <span class="text-muted-foreground text-xs font-normal"
          >Contact Point</span
        >
      </template>
    </DataListItemTitle>
    <div class="mt-2 flex flex-col gap-1 text-xs">
      <div v-if="props.contact.email" class="flex items-center gap-1">
        <icon-mail class="size-3.5" />
        <span class="truncate">
          {{ props.contact.email }}
        </span>
      </div>
      <div v-if="props.contact.mobilePhone" class="flex items-center gap-1">
        <icon-smartphone class="size-3.5" />
        <span class="truncate">{{
          formatPhoneNumber(props.contact.mobilePhone)
        }}</span>
      </div>
      <div v-if="props.contact.directLine" class="flex items-center gap-1">
        <icon-phone class="size-3.5" />
        <span class="truncate">
          {{ formatPhoneNumber(props.contact.directLine) }}
        </span>
      </div>
      <div
        v-if="errorMessages.length > 0"
        class="bg-error/10 border-error/20 mt-2 flex items-start gap-1 rounded-md p-2 pr-3"
      >
        <IconError class="size-5" />
        <span class="text-muted-foreground whitespace-pre-line text-xs">
          {{ errorMessages.join('\r\n') }}
        </span>
      </div>
    </div>
    <template #actions>
      <slot name="actions">
        <DataListItemActions>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <DataListItemAction />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <ContactDetailsBtnShow
                :contact="props.contact"
                label="Show Details"
                :icon="IconEye"
                :stakeholder-id="props.contact.stakeholderId"
                :stakeholder-name="props.contact.stakeholderName"
                :stakeholder-short-name="props.contact.stakeholderShortName"
                :stakeholder-type="props.contact.stakeholderType"
              />
              <DropdownMenuItem
                :data-testid="
                  testIds.stakeholders.contacts.actions.editContactPoint
                "
                @click="
                  modals.showEditContactModal({
                    stakeholderId: props.contact.stakeholderId,
                    stakeholderName: props.contact.stakeholderName,
                    stakeholderShortName: props.contact.stakeholderShortName,
                    stakeholderType: props.contact.stakeholderType,
                    contactType: 'other',
                    contact: props.contact
                  })
                "
              >
                <IconEdit class="size-4" />
                Edit
              </DropdownMenuItem>
              <ContactPersonBtnDelete
                :contact="props.contact"
                label="Delete"
                :icon="IconTrash"
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </DataListItemActions>
      </slot>
    </template>
  </DataListItem>
</template>
